import { type KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import {
  Button,
  ButtonGroup,
  Dialog,
  Group,
  Heading,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  Popover,
  Select,
  SelectValue,
  TextField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { PlanTypeTag } from '../../../accounts/components/PlanTypeTag';
import { SubscriptionStateTag } from '../../../accounts/components/SubscriptionStateTag';
import { type Account, SubscriptionState } from '../../../accounts/model';
import type { RequestError } from '../../request';
import { useTheme } from '../ThemeProvider';

import './styles.scss';

// Helper function to format subscription state with spaces
const formatSubscriptionState = (state: string): string =>
  state
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between camelCase words
    .replace(/[_-]/g, ' ') // Replace underscores and dashes with spaces
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export type SearchResult<T = unknown> = {
  id: string;
  title: string;
  subtitle?: string;
  icon?: string;
  data: T; // The actual data object (Account, Project, etc.)
};

export type OmnibarProps = {
  initialQuery?: string;
  isOpen: boolean;
  onClose: () => void;
  onSearch: (query: string) => void;
  searchResults: SearchResult[];
  isLoading?: boolean;
  error?: RequestError | null;
  onRetry?: () => void;
  relatedIdAccountSearchEnabled?: boolean;
  subscriptionFilter?: SubscriptionState;
  onSubscriptionFilterChange?: (filter?: SubscriptionState) => void;
  enableFilters?: boolean;
  enablePlanTypeInSearch?: boolean;
};

export function Omnibar({
  initialQuery = '',
  isOpen,
  onClose,
  onSearch,
  searchResults,
  isLoading = false,
  error = null,
  onRetry,
  relatedIdAccountSearchEnabled = false,
  subscriptionFilter,
  onSubscriptionFilterChange,
  enableFilters = false,
  enablePlanTypeInSearch = false,
}: OmnibarProps) {
  const [query, setQuery] = useState(initialQuery);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [prevOpen, setPrevOpen] = useState(isOpen);
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (isOpen) {
      setQuery('');
      setSelectedKey(null);
    }
  }
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Filter results by subscription state if filter is active
  const filteredResults = useMemo(() => {
    if (!subscriptionFilter || !enableFilters) {
      return searchResults;
    }

    return searchResults.filter((result) => {
      const account = result.data as Account;
      return account.subscription?.state === subscriptionFilter;
    });
  }, [searchResults, subscriptionFilter, enableFilters]);

  const [prevFilteredResults, setPrevFilteredResults] = useState<SearchResult[] | null>(null);
  if (prevFilteredResults !== filteredResults) {
    setPrevFilteredResults(filteredResults);
    if (
      !relatedIdAccountSearchEnabled ||
      (!isLoading && !error && !filteredResults.some((result) => result.id === selectedKey))
    ) {
      setSelectedKey(filteredResults[0]?.id ?? null);
    }
  }

  // Scroll selected item into view
  useEffect(() => {
    if (selectedKey) {
      const selectedElement = document.getElementById(selectedKey);
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [selectedKey]);

  // Handle search input changes
  const handleQueryChange = useCallback(
    (value: string) => {
      if (!relatedIdAccountSearchEnabled || value.trim() !== query.trim()) {
        setSelectedKey(null);
      }
      setQuery(value);
      onSearch(value);
    },
    [onSearch, query, relatedIdAccountSearchEnabled],
  );

  // Handle result selection
  const handleResultSelect = useCallback(
    (result: SearchResult) => {
      if (
        relatedIdAccountSearchEnabled &&
        (isLoading || error || !filteredResults.some((current) => current.id === result.id))
      ) {
        return;
      }
      onClose();

      // Navigate to account (since all results are accounts)
      navigate(`/accounts/${result.id}`);
    },
    [relatedIdAccountSearchEnabled, isLoading, error, filteredResults, navigate, onClose],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const totalResults = filteredResults.length;

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const currentIndex = selectedKey ? filteredResults.findIndex((r) => r.id === selectedKey) : -1;
          const nextIndex = Math.min(currentIndex + 1, totalResults - 1);
          if (filteredResults[nextIndex]) {
            setSelectedKey(filteredResults[nextIndex].id);
          }
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const currentUpIndex = selectedKey ? filteredResults.findIndex((r) => r.id === selectedKey) : 0;
          const prevIndex = Math.max(currentUpIndex - 1, 0);
          if (filteredResults[prevIndex]) {
            setSelectedKey(filteredResults[prevIndex].id);
          }
          break;
        }
        case 'Enter': {
          event.preventDefault();
          const selectedResult = filteredResults.find((r) => r.id === selectedKey);
          if (selectedResult) {
            handleResultSelect(selectedResult);
          }
          break;
        }
        case 'Escape': {
          event.preventDefault();
          onClose();
          break;
        }
        default: {
          break;
        }
      }
    },
    [filteredResults, selectedKey, onClose, handleResultSelect],
  );

  // Handle subscription filter changes
  const handleSubscriptionFilterChange = useCallback(
    (key: string | number | null) => {
      const value = key as string;
      const filter = value === 'all' ? undefined : (value as SubscriptionState);
      onSubscriptionFilterChange?.(filter);
    },
    [onSubscriptionFilterChange],
  );

  // Render subscription filter
  const renderSubscriptionFilter = () => {
    if (!enableFilters || !onSubscriptionFilterChange) {
      return null;
    }

    return (
      <div className="omnibar-subscription-filter">
        <Group className="omnibar-subscription-filter__field">
          <Label>Subscription state:</Label>
          <Select
            selectedKey={subscriptionFilter || 'all'}
            onSelectionChange={handleSubscriptionFilterChange}
            className="omnibar-subscription-filter__select"
            aria-label="Filter by subscription state"
          >
            <Button>
              <SelectValue />
            </Button>
            <Popover>
              <ListBox>
                <ListBoxItem id="all" textValue="All">
                  All
                </ListBoxItem>
                {Object.values(SubscriptionState).map((state) => (
                  <ListBoxItem key={state} id={state} textValue={formatSubscriptionState(state)}>
                    {formatSubscriptionState(state)}
                  </ListBoxItem>
                ))}
              </ListBox>
            </Popover>
          </Select>
        </Group>
      </div>
    );
  };

  // Render individual result item using LaunchPad UI Button
  const renderResultItem = (result: SearchResult) => {
    const account = result.data as Account;
    return (
      <Button
        key={result.id}
        id={result.id}
        variant="minimal"
        size="medium"
        onPress={() => handleResultSelect(result)}
        isDisabled={relatedIdAccountSearchEnabled && (isLoading || error !== null)}
        className={`omnibar-result-button ${selectedKey === result.id ? 'omnibar-result-button--selected' : ''}`}
        onFocus={() => setSelectedKey(result.id)}
      >
        <div className="omnibar-result-item">
          <div className="omnibar-result-item__content">
            <div className="omnibar-result-item__title">
              {result.title}
              {enableFilters && account?.subscription && <SubscriptionStateTag state={account.subscription.state} />}
              {enablePlanTypeInSearch && account?.subscription?.planType && (
                <PlanTypeTag planType={account.subscription.planType} />
              )}
            </div>
          </div>
        </div>
      </Button>
    );
  };

  // Render results
  const renderResults = () => {
    if (isLoading) {
      return (
        <div className="omnibar-results__loading" role="status">
          <Icon name="sync" />
          <div className="omnibar-results__loading-text">
            <Message id="vital.accountSearch.loading" />
          </div>
        </div>
      );
    }

    if (relatedIdAccountSearchEnabled && error) {
      const unauthorized = [401, 403, 499].includes(error.status);
      return (
        <div className="omnibar-results__empty" role="alert">
          <p>
            <Message id={unauthorized ? 'vital.accountSearch.unauthorized' : 'vital.accountSearch.loadError'} />
          </p>
          {!unauthorized && onRetry && <Button onPress={onRetry}>{msg('vital.accountSearch.retry')}</Button>}
        </div>
      );
    }

    if (!(relatedIdAccountSearchEnabled ? query.trim() : query)) {
      return (
        <div className="omnibar-results__placeholder">
          <div className="omnibar-results__placeholder-title">
            <Message
              id={relatedIdAccountSearchEnabled ? 'vital.accountSearch.help' : 'vital.accountSearch.legacyHelp'}
            />
          </div>
        </div>
      );
    }

    if (relatedIdAccountSearchEnabled && query.trim().length < 2) {
      return (
        <div className="omnibar-results__empty">
          <Message id="vital.accountSearch.minimumInput" />
        </div>
      );
    }

    if (filteredResults.length === 0) {
      return <div className="omnibar-results__empty">No results found for "{query}"</div>;
    }

    return (
      <div className="omnibar-results">
        <div className="omnibar-results-list" role="list" aria-label="Search results">
          {filteredResults.map((result) => renderResultItem(result))}
        </div>
      </div>
    );
  };

  return (
    <div data-theme={theme}>
      <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
        <Modal size="medium" className="omnibar-modal">
          <Dialog>
            <Heading slot="title" className="omnibar-hidden-heading">
              Search
            </Heading>
            <div className="omnibar-content">
              {/* Search Input */}
              <div className="omnibar-search">
                <ButtonGroup className="omnibar-search__input-group">
                  <Button variant="default" className="omnibar-search__icon-button">
                    <Icon name="search" />
                  </Button>
                  <TextField value={query} onChange={handleQueryChange} className="omnibar-search__text-field">
                    <Label style={{ display: 'none' }}>Search</Label>
                    <Input
                      placeholder="Search..."
                      onKeyDown={handleKeyDown}
                      autoFocus
                      className="omnibar-search__input"
                    />
                  </TextField>
                </ButtonGroup>
              </div>

              {/* Subscription Filter */}
              {renderSubscriptionFilter()}

              {/* Results */}
              <div className="omnibar-results">{renderResults()}</div>

              {/* Footer */}
              <div className="omnibar-footer">
                <span>Use ↑↓ to navigate, Enter to select, Esc to close</span>
                <span>Cmd+K to open</span>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </div>
  );
}

export default Omnibar;
